<template>
  <component
    :is="type === 'toggle' ? 'div' : 'label'"
    :class="[
      $style.root,
      {
        [$style.vertical]: captionPosition === 'vertical',
        [$style.clickable]: !disabled && (type === 'action' || type === 'toggle'),
        [$style.isDisabled]: disabled,
      },
    ]"
    @click.capture="clicked"
  >
    <div
      :class="[
        $style.itemDiv,
        {
          [$style.vertical]: captionPosition === 'vertical',
          [$style.isDisabled]: disabled,
          [$style.selected]: selected,
          [$style.active]: active,
          [$style.isRadio]: type === 'radio',
          [$style.isToggle]: type === 'toggle',
          [$style.useSmallerVerticalPadding]: useSmallerVerticalPadding,
          [$style.useSmallPadding]: useSmallPadding,
          [$style.useHigherOption]: useHigherOption,
          [$style.unsetAll]: unsetAll,
          [$style.unsetGap]: unsetGap,
          [$style.useNeutralIconFill]: useNeutralIconFill,
        },
      ]"
    >
      <div
        :class="[
          $style.contentDiv,
          {
            [$style.selectPadding]: !selected && type === 'selectable',
          },
        ]"
      >
        <button v-if="type === 'dropdowntarget'" />
        <SCASvgIcon v-if="type === 'selectable' && selected" icon="phosphor/check" />
        <SCACheckboxBase v-if="type === 'checkbox'" :checked="checked" :disabled="disabled" :class="$style.checkbox">
          <slot />
        </SCACheckboxBase>
        <SCARadioButtonBase v-if="type === 'radio'" :checked="checked" :disabled="disabled" :class="$style.radio">
          <slot />
        </SCARadioButtonBase>
        <SCASvgIcon v-if="icon" :icon="icon" :size="iconSize" />
        <SCAAvatar v-if="$slots.avatar || avatarInitials" size="xsmall" :initials="avatarInitials">
          <slot name="avatar" />
        </SCAAvatar>
        <div :class="[$style.textDiv, { [$style.withLargeIcon]: icon && iconSize === '20px' }]">
          <span :class="$style.label">
            <slot name="tag">
              <slot name="label">{{ label }}</slot>
            </slot>
          </span>
          <span v-if="caption && captionPosition === 'vertical'" :class="$style.caption">{{ caption }}</span>
        </div>
        <SCAToggle
          v-if="type === 'toggle'"
          :model-value="checked"
          :disabled="disabled"
          :class="$style.checkbox"
          no-padding
          :no-gap="unsetGap"
        />
      </div>
      <span v-if="caption && captionPosition === 'horizontal'" :class="$style.caption">{{ caption }}</span>
      <SCASvgIcon v-if="expandable" icon="phosphor/caret-right" :size="iconSize" />
    </div>
  </component>
</template>

<script setup lang="ts">
import type { IconName } from "../icons/iconNames.generated";
import SCAAvatar from "../SCAAvatar.vue";
import SCACheckboxBase from "../SCACheckboxBase.vue";
import SCARadioButtonBase from "../SCARadioButtonBase.vue";
import SCASvgIcon from "../SCASvgIcon.vue";
import SCAToggle from "../SCAToggle.vue";

const props = withDefaults(
  defineProps<{
    type?: "selectable" | "checkbox" | "radio" | "action" | "toggle" | "dropdowntarget";
    label: string;
    icon?: IconName;
    avatarInitials?: string;
    captionPosition?: "horizontal" | "vertical";
    caption?: string;
    expandable?: boolean;
    checked?: boolean;
    disabled?: boolean;
    active?: boolean;
    useSmallerVerticalPadding?: boolean;
    useSmallPadding?: boolean;
    useHigherOption?: boolean;
    iconSize?: string;
    unsetAll?: boolean;
    unsetGap?: boolean;
    useNeutralIconFill?: boolean;
  }>(),
  {
    type: undefined,
    icon: undefined,
    avatarInitials: undefined,
    captionPosition: "horizontal",
    caption: undefined,
    expandable: false,
    checked: false,
    disabled: false,
    active: false,
    useSmallerVerticalPadding: false,
    useSmallPadding: false,
    useHigherOption: false,
    iconSize: "20px",
    unsetAll: false,
    unsetGap: false,
    useNeutralIconFill: false,
  },
);
const selected = defineModel<boolean>("selected", {
  default: false,
});
const clicked = (event: MouseEvent) => {
  if (props.disabled) {
    event.stopPropagation();
    event.preventDefault();
    return;
  }
  if (props.type === "selectable") {
    selected.value = !selected.value;
  }
};
</script>

<style module lang="scss">
@use "../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../../styles/icon.scss" as IconMixins;

.root {
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  white-space: nowrap;

  &.vertical {
    height: var(--size-large);
  }
  &.clickable {
    cursor: pointer;
  }

  &.isDisabled {
    opacity: 0.5;
  }
}

.itemDiv {
  display: flex;
  padding: 6px var(--space-component-padding-small);
  align-items: center;
  flex: 1 0 0;
  gap: var(--space-component-gap-large);
  width: 100%;

  border-radius: var(--border-radius-large);
  background: var(--color-interaction-ghost-normal);
  color: var(--color-content-primary);
  transition: background 250ms ease-in-out;

  &.vertical {
    height: var(--size-large);
  }

  &:not(.isDisabled) {
    &:hover {
      background-color: var(--color-action-ghost-hover);
    }

    &:focus {
      background: var(--color-interaction-background-modeless);

      /* Focus/Default Focus */
      box-shadow: 0 0 0 2px var(--color-interaction-focus-default);
    }
  }

  &.useNeutralIconFill {
    svg {
      @include IconMixins.icon-color(var(--color-action-neutral-normal));
    }
  }

  &.selected {
    background: var(--color-interaction-default-subtle-hover);
    color: var(--color-interaction-default-active);

    svg {
      @include IconMixins.icon-color(var(--color-interaction-default-active));
    }

    &:hover {
      background: var(--color-interaction-default-subtle-hover);
    }
  }

  // For a trigger whose own sub-menu is open — looks like a plain hover rather than a distinct
  // selected state, since the hover rule above already uses this color.
  &.active {
    background-color: var(--color-action-ghost-hover);
  }

  &.isRadio {
    padding: var(--space-component-padding-xSmall);
  }

  &.isToggle {
    .checkbox svg {
      width: 12px;
      height: 12px;
    }

    &:not(.isDisabled):hover {
      background-color: var(--color-action-ghost-hover);
    }
  }

  &.useSmallerVerticalPadding {
    padding-top: 4px;
    padding-bottom: 4px;
  }

  &.useSmallPadding {
    padding: var(--space-component-padding-small) var(--space-component-padding-xSmall);
  }

  &.useHigherOption {
    padding-left: var(--space-component-padding-xSmall);
    padding-right: var(--space-component-padding-xSmall);
    border-radius: var(--border-radius-large);
    height: var(--size-small);
    width: 188px;
  }

  &.unsetAll {
    all: unset;
    width: 100%;

    &:hover {
      all: unset;
      width: 100%;
    }
  }

  &.unsetGap {
    gap: unset;
  }
}

.contentDiv {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
  flex: 1 0 0;
  overflow: hidden;

  &.selectPadding {
    padding-left: var(--space-component-padding-5xLarge);
  }
}

.gapSmaller {
  gap: var(--space-component-gap-small);
  cursor: pointer;
}

.textDiv {
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
}
.withLargeIcon {
  .label {
    line-height: 20px;
  }
}
.label {
  @include FigmaTypoMixins.typo-body-medium;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  min-height: 1lh;
}
.caption {
  @include FigmaTypoMixins.typo-body-small;
  color: var(--color-content-secondary);
}

.paddingUnset {
  padding: var(--space-component-padding-null);
  height: unset;
  margin-top: var(--space-component-padding-xSmall);
}
</style>
